import { useEffect, useRef, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { useAuth, useLang } from "../lib/context";
import { buildMeta } from "../lib/cond";
import { Field } from "../components/FieldRenderer";
import { Card, Btn, Loading } from "../components/Shell";
import { StatusBadge, EnvBadge } from "../components/StatusBadge";
import DocumentsStep from "../components/DocumentsStep";
import { ReviewStep, ConsentStep, VerifyStep } from "../components/FinalSteps";
import { Check, CheckCircle2 as CloudCheck, CloudUpload, AlertTriangle, ArrowLeft, ArrowRight, Landmark, Building2, BadgeCheck, CreditCard, QrCode, Smartphone, ShieldCheck, Lightbulb } from "lucide-react";

const KIND = { savings: ["Savings Account", Landmark], current: ["Current Account", Building2], existing_account: ["Existing Account Verification", BadgeCheck], pg: ["Payment Gateway", CreditCard], qr: ["Merchant QR", QrCode], pos: ["POS", Smartphone] };

function Ring({ pct }) {
  const r = 30, c = 2 * Math.PI * r;
  return (
    <div className="relative h-20 w-20 shrink-0">
      <svg viewBox="0 0 72 72" className="h-20 w-20 -rotate-90">
        <circle cx="36" cy="36" r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="7" />
        <circle cx="36" cy="36" r={r} fill="none" stroke="url(#wz-g)" strokeWidth="7" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} className="transition-[stroke-dashoffset] duration-700" />
        <defs><linearGradient id="wz-g"><stop offset="0" stopColor="#C6F432" /><stop offset="1" stopColor="#FFD166" /></linearGradient></defs>
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-extrabold leading-none" data-testid="progress-percent">{pct}%</span>
        <span className="mt-0.5 text-[9px] font-semibold uppercase tracking-wider text-slate-400">done</span>
      </div>
    </div>
  );
}
import { toast } from "sonner";

function Stepper({ steps, current, status, onGo, lang, vertical }) {
  return (
    <ol className={vertical ? "space-y-2" : "flex gap-2 overflow-x-auto pb-1"} data-testid={vertical ? "wizard-stepper" : "wizard-stepper-mobile"}>
      {steps.map((s, i) => {
        const on = i === current, done = status[s.id];
        const st = on ? "border-violet-200 bg-violet-50 text-violet-900 font-semibold shadow-sm" : done ? "border-transparent text-slate-800 hover:bg-slate-50" : "border-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-800";
        return (
          <li key={s.id} className="relative">
            {vertical && i < steps.length - 1 && <span className={`absolute left-[25px] top-[38px] h-[calc(100%-22px)] w-px ${done ? "bg-lime-400" : "bg-slate-200"}`} />}
            <button onClick={() => onGo(i)} data-testid={vertical ? `step-${s.id}` : `mstep-${s.id}`} aria-current={on ? "step" : undefined}
              className={`relative flex w-full min-h-[44px] items-center gap-3 whitespace-nowrap rounded-xl border px-3 py-2 text-left text-sm transition-colors duration-200 ${st}`}>
              <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[11px] font-bold ring-4 ring-white ${on ? "sx-grad text-white shadow-md shadow-fuchsia-500/30" : done ? "bg-[#C6F432] text-[#0E0B1F]" : "border border-slate-300 bg-white text-slate-500"}`}>{done && !on ? <Check className="h-3.5 w-3.5" /> : i + 1}</span>
              <span className={vertical ? "truncate" : ""}>{lang === "hi" && s.title_hi ? s.title_hi : s.title}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export default function Wizard() {
  const { id } = useParams();
  const nav = useNavigate();
  const { config } = useAuth();
  const { lang, t } = useLang();
  const [d, setD] = useState(null);
  const [data, setData] = useState({});
  const [cur, setCur] = useState(0);
  const [errors, setErrors] = useState({});
  const [saveState, setSaveState] = useState("saved");
  const [extra, setExtra] = useState({ outlets: [], accounts: [] });
  const pending = useRef({});
  const timer = useRef(null);

  const load = useCallback(async () => {
    const r = await api.get(`/applications/${id}`);
    setD(r.data);
    setData(r.data.application.data);
    return r.data;
  }, [id]);

  useEffect(() => {
    load().then((x) => {
      setCur(Math.min(x.application.current_step || 0, x.steps.length - 1));
      const bid = x.application.business_id;
      if (bid && ["pg", "qr", "pos"].includes(x.application.kind)) {
        Promise.all([api.get(`/businesses/${bid}/outlets`), api.get(`/businesses/${bid}/settlement-accounts`)]).then(([o, a]) => setExtra({ outlets: o.data, accounts: a.data }));
      }
    }).catch((e) => toast.error(errMsg(e)));
  }, [load]);

  const flush = useCallback(async (stepId, stepIdx) => {
    clearTimeout(timer.current);
    const changes = pending.current;
    pending.current = {};
    setSaveState("saving");
    try {
      const r = await api.patch(`/applications/${id}/data`, { changes, step_id: stepId, current_step: stepIdx });
      setSaveState("saved");
      return r.data;
    } catch (e) {
      setSaveState("error");
      pending.current = { ...changes, ...pending.current };
      toast.error(errMsg(e));
      return null;
    }
  }, [id]);

  if (!d) return <Loading />;
  const app = d.application;
  const steps = d.steps;
  const st = steps[cur];
  const editable = ["draft", "correction_required"].includes(app.internal_status);
  const meta = buildMeta({ ...app, data }, d.provider);
  const ctx = { provider: d.provider, kind: app.kind, entityRules: config?.entity_rules?.[meta.$entity || "other"], roleOptions: config?.role_options || {}, entityTypes: config?.entity_types, errors, ...extra };

  const change = (k, v) => {
    setData((p) => ({ ...p, [k]: v }));
    setErrors((e) => { const n = { ...e }; delete n[k]; return n; });
    pending.current[k] = v;
    setSaveState("dirty");
    clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(st.id, cur), 900);
  };

  const go = async (i) => {
    const r = editable ? await flush(st.id, i) : null;
    if (r) setErrors(r.errors || {});
    await load();
    setCur(i);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const next = async () => {
    if (!editable) return setCur(cur + 1);
    const r = await flush(st.id, cur);
    if (!r) return;
    setErrors(r.errors || {});
    if (Object.keys(r.errors || {}).length) {
      toast.error("Please fix the highlighted fields");
      setTimeout(() => document.querySelector('[role="alert"]')?.scrollIntoView({ block: "center" }), 50);
      return;
    }
    await go(cur + 1);
  };

  const [kindLabel, KindIcon] = KIND[app.kind] || [app.kind, Landmark];
  const doneCount = steps.filter((s) => d.progress.steps[s.id]).length;
  const saveEl = saveState === "saving" ? <><CloudUpload className="h-3.5 w-3.5 animate-pulse" /> {t("Saving…")}</> : saveState === "error" ? <span className="text-rose-300">Save failed - retrying</span> : saveState === "dirty" ? t("Unsaved changes") : <><CloudCheck className="h-3.5 w-3.5 text-emerald-400" /> {t("Saved on server")}</>;

  return (
    <div className="space-y-6 pb-4">
      <section className="sx-grad relative overflow-hidden rounded-[2rem] p-6 text-white shadow-xl shadow-fuchsia-600/20 sm:p-8" data-testid="wizard-hero">
        <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#C6F432]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-56 w-56 rounded-full bg-[#0E0B1F]/35 blur-3xl" />
        <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="flex min-w-0 items-start gap-4">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/20 backdrop-blur"><KindIcon className="h-7 w-7 text-white" /></span>
            <div className="min-w-0">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#C6F432]">{d.provider?.name}{app.entity_type && app.kind !== "savings" ? " · " + (config?.entity_types.find((e) => e.code === app.entity_type)?.label || app.entity_type) : ""}</div>
              <h1 data-testid="wizard-title" className="mt-1 text-2xl font-extrabold tracking-tight sm:text-3xl">{kindLabel} application</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="rounded-lg border border-white/15 bg-white/10 px-2.5 py-1 font-mono text-xs" data-testid="app-number">{app.app_number}</span>
                <StatusBadge status={app.internal_status} testid="internal-status" />
                {d.provider?.mode === "sandbox" && <EnvBadge env="sandbox" />}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 rounded-2xl bg-[#0E0B1F]/80 p-3 pr-5 backdrop-blur">
            <Ring pct={d.progress.percent} />
            <div className="space-y-1.5">
              <div className="text-sm font-semibold" data-testid="steps-done-count">{doneCount} of {steps.length} steps complete</div>
              <div className="flex items-center gap-1.5 text-xs text-slate-300" data-testid="autosave-indicator" aria-live="polite">{saveEl}</div>
            </div>
          </div>
        </div>
      </section>
      {!editable && (
        <div className="flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="locked-banner">
          <AlertTriangle className="h-4 w-4" /> This application is submitted and locked. <Link className="font-semibold underline" to={`/applications/${id}/status`}>View status</Link>
        </div>
      )}
      {app.internal_status === "correction_required" && d.deficiencies.filter((x) => x.status === "open").length > 0 && (
        <Card className="border-purple-300 bg-purple-50" data-testid="correction-banner">
          <div className="font-semibold text-purple-900">Corrections requested</div>
          <ul className="mt-2 list-disc pl-5 text-sm text-purple-900">{d.deficiencies.filter((x) => x.status === "open").map((x) => <li key={x.id}><b>{x.target}</b>: {x.message}</li>)}</ul>
        </Card>
      )}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[17rem_minmax(0,1fr)]">
      <aside className="min-w-0 space-y-4 lg:sticky lg:top-24 lg:h-fit">
        <div className="hidden rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm lg:block">
          <div className="px-2 pb-3 pt-1 text-[11px] font-bold uppercase tracking-[0.16em] text-slate-400">Application steps</div>
          <Stepper vertical steps={steps} current={cur} status={d.progress.steps} onGo={go} lang={lang} />
        </div>
        <div className="lg:hidden"><Stepper steps={steps} current={cur} status={d.progress.steps} onGo={go} lang={lang} /></div>
        <div className="hidden rounded-3xl bg-[#0E0B1F] p-5 text-xs leading-relaxed text-slate-300 lg:block" data-testid="wizard-help-card">
          <div className="mb-1 flex items-center gap-2 font-semibold text-white"><Lightbulb className="h-4 w-4 text-[#C6F432]" /> Good to know</div>
          You can jump between steps anytime. Your answers save automatically, so you can leave and resume later.
        </div>
      </aside>
      <div className="min-w-0 space-y-6">
      <Card className="p-0 sm:p-0">
        <div className="flex items-center gap-4 border-b border-slate-100 px-5 py-5 sm:px-10 sm:py-7">
          <span className="sx-grad flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-sm font-extrabold text-white shadow-md shadow-fuchsia-500/30">{cur + 1}</span>
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-violet-600">Step {cur + 1} of {steps.length}</div>
            <h2 className="text-xl font-extrabold tracking-tight sm:text-2xl" data-testid="step-heading">{lang === "hi" && st.title_hi ? st.title_hi : st.title}</h2>
          </div>
          <span className="ml-auto hidden items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 sm:flex"><ShieldCheck className="h-3.5 w-3.5" /> Secure & auto-saved</span>
        </div>
        <div className="px-5 py-6 sm:px-10 sm:py-8">
        {st.kind === "form" && (
          <fieldset disabled={!editable} className="grid gap-5 sm:grid-cols-2">
            {st.fields.map((f) => <Field key={f.key} f={f} value={data[f.key]} data={data} meta={meta} ctx={ctx} error={errors[f.key]} onChange={(v) => change(f.key, v)} />)}
          </fieldset>
        )}
        {st.kind === "verify" && <VerifyStep d={d} reload={load} editable={editable} />}
        {st.kind === "documents" && <DocumentsStep d={d} reload={load} editable={editable} />}
        {st.kind === "review" && <ReviewStep d={d} data={data} meta={meta} onEdit={(i) => go(i)} />}
        {st.kind === "consent" && <ConsentStep d={d} reload={load} editable={editable} onDone={() => nav(`/applications/${id}/status?ack=1`)} />}
        </div>
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-b-3xl border-t border-slate-100 bg-white/95 px-5 py-4 backdrop-blur sm:px-10" data-testid="wizard-action-bar">
          <Btn variant="outline" data-testid="wizard-back-btn" disabled={cur === 0} onClick={() => go(cur - 1)}><ArrowLeft className="h-4 w-4" /> {t("Back")}</Btn>
          <div className="flex gap-2 sm:gap-3">
            <Btn variant="ghost" data-testid="wizard-save-exit-btn" onClick={async () => { if (editable) await flush(st.id, cur); nav("/resume"); }}>{t("Save & exit")}</Btn>
            {cur < steps.length - 1 && <Btn data-testid="wizard-next-btn" onClick={next}>{t("Next")} <ArrowRight className="h-4 w-4" /></Btn>}
          </div>
        </div>
      </Card>
      </div>
      </div>
    </div>
  );
}
